import {
  CriteriaWithPagination,
  EuiBasicTable,
  EuiBasicTableColumn,
  EuiButton,
  EuiPage,
  EuiPageBody,
  EuiPageContent,
  EuiPageContentBody,
  EuiPageHeader,
  EuiPanel,
  EuiSpacer,
  EuiTableSortingType,
} from "@elastic/eui";
import moment from "moment";
import { useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import authAxios from "services/authAxios";
import pageAndSort from "services/pageAndSort";
import { UploadDocument } from "types/apiTypes";

interface TableData {
  recordID: number;
  type: string;
  comments: string;
  timestamp: string;

  doc: UploadDocument;
}

const getUploadedDocuments = async (teamID: string) => {
  const { data } = await authAxios.post(`/uploaded_documents`, { teamID: teamID });
  return data;
};

function useUploadedDocuments(teamID: string) {
  const useQueryReturn = useQuery<UploadDocument[]>(
    ["uploaded_documents", { teamID: teamID }],
    () => getUploadedDocuments(teamID),
    {
      placeholderData: [],
      initialData: [],
      initialDataUpdatedAt: 0,
      staleTime: 1000 * 60 * 15,
    }
  );
  return { ...useQueryReturn, data: useQueryReturn.data! };
}

const UploadedDocumentsAgent: React.FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data, isLoading } = useUploadedDocuments("All");

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [showPerPageOptions] = useState(true);
  const [sortField, setSortField] = useState<keyof TableData>("timestamp");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  const dataTable: TableData[] = data.map((record) => {
    return {
      recordID: record.recordID,
      type: record.type,
      comments: record.comments,
      timestamp: record.timestamp,
      numFiles: record.files.length,

      doc: record,
    };
  });

  const columns: EuiBasicTableColumn<TableData>[] = [
    {
      field: "type",
      name: "Type",
      width: "100px",
    },
    {
      field: "comments",
      name: "Comments",
    },
    {
      field: "numFiles",
      name: "Document Count",
    },
    {
      field: "timestamp",
      name: "Upload Date",
      width: "100px",
      render: (date: string) => moment(date).format("l"),
    },
  ];

  const { pageOfItems, totalItemCount } = pageAndSort(dataTable, { sortField, sortDirection, pageIndex, pageSize });
  const getRowProps = (record: TableData) => {
    const { recordID } = record.doc;
    return {
      "data-test-subj": `row=${recordID}`,
      className: "customRowClass",
      onClick: () => {
        navigate(`/uploaded_document/${recordID}`);
      },
    };
  };

  const getCellProps = (record: TableData, column: any) => {
    const { recordID } = record.doc;
    const { field } = column;
    return {
      className: "customCellClass",
      "data-test-subj": `cell-${recordID}-${field}`,
      textOnly: true,
    };
  };

  const sorting: EuiTableSortingType<TableData> = {
    sort: {
      field: sortField,
      direction: sortDirection,
    },
    enableAllColumns: true,
  };

  const pagination = {
    pageIndex,
    pageSize,
    totalItemCount,
    pageSizeOptions: [20, 50, 100],
    hidePerPageOptions: !showPerPageOptions,
  };

  const onTableChange = ({ page, sort }: CriteriaWithPagination<TableData>) => {
    const { index: pageIndex, size: pageSize } = page;
    if (sort) {
      const { field: sortField, direction: sortDirection } = sort;
      setSortField(sortField);
      setSortDirection(sortDirection);
    }
    setPageIndex(pageIndex);
    setPageSize(pageSize);
  };

  return (
    <EuiPage>
      <EuiPageBody>
        <EuiPageHeader
          pageTitle="Upload Transaction History"
          restrictWidth="1200px"
          rightSideItems={[
            <EuiButton
              onClick={() => {
                queryClient.invalidateQueries();
                navigate("/upload_documents");
              }}
            >
              New Upload
            </EuiButton>,
          ]}
        />

        <EuiSpacer size="m" />
        <EuiPageContent hasBorder={false} hasShadow={false} paddingSize="none" color="transparent" borderRadius="none">
          <EuiPageContentBody restrictWidth="1200px">
            <EuiPanel hasBorder>
              <EuiBasicTable
                items={pageOfItems}
                itemId="uploads"
                columns={columns}
                rowProps={getRowProps}
                cellProps={getCellProps}
                pagination={pagination}
                onChange={onTableChange}
                sorting={sorting}
                loading={isLoading}
              />
            </EuiPanel>
          </EuiPageContentBody>
        </EuiPageContent>
      </EuiPageBody>
    </EuiPage>
  );
};

export default UploadedDocumentsAgent;
